import tv4Img from "@/assets/products/archive/tv4.jpg";
import tv4kUltraImg from "@/assets/products/archive/4k-ultra.jpg";
import tv3sImg from "@/assets/products/archive/tv3s.jpg";
import tv3Img from "@/assets/products/archive/tv3.jpg";
import tv4k2Img from "@/assets/products/archive/4k2.jpg";
import tv2er2Img from "@/assets/products/archive/tv2er2.jpg";
import tv4kImg from "@/assets/products/archive/4k.jpg";
import tv2eImg from "@/assets/products/archive/tv2e.jpg";
import tv2Img from "@/assets/products/archive/tv2.jpg";
import tvImg from "@/assets/products/archive/tv.jpg";
import kid3dImg from "@/assets/products/archive/3d-kid.jpg";
import hd1Img from "@/assets/products/archive/hd1.jpg";
import type { Locale } from "@/lib/site";

// Canonical spec rows. The data below carries spec *values* in a single
// (technical, language-neutral) form; only the row labels are localized, via
// SPEC_LABELS. Keep the union and SPEC_LABELS in sync.
export type ArchiveSpecKey =
  | "cpu"
  | "ram"
  | "storage"
  | "os"
  | "resolution"
  | "videoCodecs"
  | "audioCodecs"
  | "hdmi"
  | "usb"
  | "audioOut"
  | "network"
  | "wifi"
  | "bluetooth"
  | "cardReader"
  | "dimensions"
  | "power"
  | "accessories";

export const SPEC_LABELS: Record<ArchiveSpecKey, Record<Locale, string>> = {
  cpu: { en: "CPU", uk: "Процесор", pl: "Procesor", pt: "CPU", es: "CPU" },
  ram: { en: "RAM", uk: "Оперативна пам'ять", pl: "Pamięć RAM", pt: "RAM", es: "RAM" },
  storage: {
    en: "Storage",
    uk: "Накопичувач",
    pl: "Pamięć",
    pt: "Armazenamento",
    es: "Almacenamiento",
  },
  os: {
    en: "Operating system",
    uk: "Операційна система",
    pl: "System operacyjny",
    pt: "Sistema operacional",
    es: "Sistema operativo",
  },
  resolution: {
    en: "Video / resolution",
    uk: "Відео / роздільність",
    pl: "Wideo / rozdzielczość",
    pt: "Vídeo / resolução",
    es: "Video / resolución",
  },
  videoCodecs: {
    en: "Video codecs",
    uk: "Відеокодеки",
    pl: "Kodeki wideo",
    pt: "Codecs de vídeo",
    es: "Códecs de video",
  },
  audioCodecs: {
    en: "Audio codecs",
    uk: "Аудіокодеки",
    pl: "Kodeki audio",
    pt: "Codecs de áudio",
    es: "Códecs de audio",
  },
  hdmi: { en: "HDMI", uk: "HDMI", pl: "HDMI", pt: "HDMI", es: "HDMI" },
  usb: { en: "USB", uk: "USB", pl: "USB", pt: "USB", es: "USB" },
  audioOut: {
    en: "Audio / AV output",
    uk: "Аудіо / AV-виходи",
    pl: "Wyjścia audio / AV",
    pt: "Saída de áudio / AV",
    es: "Salida de audio / AV",
  },
  network: { en: "Network", uk: "Мережа", pl: "Sieć", pt: "Rede", es: "Red" },
  wifi: { en: "Wi-Fi", uk: "Wi-Fi", pl: "Wi-Fi", pt: "Wi-Fi", es: "Wi-Fi" },
  bluetooth: {
    en: "Bluetooth",
    uk: "Bluetooth",
    pl: "Bluetooth",
    pt: "Bluetooth",
    es: "Bluetooth",
  },
  cardReader: {
    en: "Card reader",
    uk: "Кардридер",
    pl: "Czytnik kart",
    pt: "Leitor de cartões",
    es: "Lector de tarjetas",
  },
  dimensions: {
    en: "Dimensions",
    uk: "Розміри",
    pl: "Wymiary",
    pt: "Dimensões",
    es: "Dimensiones",
  },
  power: { en: "Power", uk: "Живлення", pl: "Zasilanie", pt: "Alimentação", es: "Alimentación" },
  accessories: {
    en: "In the box",
    uk: "Комплектація",
    pl: "Zawartość zestawu",
    pt: "Conteúdo da caixa",
    es: "Contenido de la caja",
  },
};

export type ArchiveModel = {
  id: string;
  image: string;
  // Approximate catalog year, taken from the original inext.ltd product listing.
  // Used for ordering and the era badge — not a hard release date.
  year: number;
  en: {
    title: string;
    shortDesc: string;
  };
  localized?: Partial<Record<Locale, { title?: string; shortDesc?: string }>>;
  // Ordered: object key order is the display order in the spec table.
  specs: Partial<Record<ArchiveSpecKey, string>>;
};

// Newest → oldest. This order is also the on-page order.
export const archiveModels: ArchiveModel[] = [
  {
    id: "tv4",
    image: tv4Img,
    year: 2019,
    en: {
      title: "inext TV4",
      shortDesc:
        "Android 7 Nougat 4K box on a quad-core Allwinner H3 with 1 GB RAM and 8 GB storage.",
    },
    localized: {
      uk: {
        shortDesc:
          "4K-приставка на Android 7 Nougat: чотириядерний Allwinner H3, 1 ГБ ОЗП, 8 ГБ пам'яті.",
      },
      pl: {
        shortDesc:
          "Dekoder 4K z Androidem 7 Nougat: czterordzeniowy Allwinner H3, 1 GB RAM, 8 GB pamięci.",
      },
      pt: {
        shortDesc:
          "Box 4K com Android 7 Nougat: Allwinner H3 quad-core, 1 GB de RAM e 8 GB de armazenamento.",
      },
      es: {
        shortDesc:
          "Box 4K con Android 7 Nougat: Allwinner H3 quad-core, 1 GB de RAM y 8 GB de almacenamiento.",
      },
    },
    specs: {
      cpu: "Allwinner H3 Quad-Core 1.0 GHz",
      ram: "1 GB DDR3",
      storage: "8 GB NAND Flash",
      os: "Android 7.0.1+",
      resolution: "4K UHD (2160p) up to 3840×2160 @ 30 fps",
      videoCodecs: "H.265 (HEVC), H.264, MPEG1/2/4, VC-1, Xvid/DivX, RealVideo",
      audioCodecs: "MP3, WMA, AAC, WAV, OGG, MIDI, FLAC, APE, ALAC, M4A",
      hdmi: "HDMI v1.4",
      usb: "2× USB 2.0 Host",
      audioOut: "RCA via 3.5 mm mini-jack",
      network: "Ethernet 100 Mb/s",
      wifi: "2.4 GHz Wi-Fi b/g/n",
      cardReader: "microSD / TF",
      dimensions: "103 × 103 × 21 mm",
      power: "5 V / 2 A",
      accessories: "Remote, power adapter, HDMI cable, AV cable, quick start guide",
    },
  },
  {
    id: "4k-ultra",
    image: tv4kUltraImg,
    year: 2019,
    en: {
      title: "inext 4K Ultra",
      shortDesc: "Top of the Allwinner line — Android 7, 4K UHD, 2 GB RAM and 16 GB storage.",
    },
    localized: {
      uk: {
        shortDesc: "Флагман лінійки Allwinner — Android 7, 4K UHD, 2 ГБ ОЗП та 16 ГБ пам'яті.",
      },
      pl: {
        shortDesc: "Topowy model linii Allwinner — Android 7, 4K UHD, 2 GB RAM i 16 GB pamięci.",
      },
      pt: {
        shortDesc:
          "Topo da linha Allwinner — Android 7, 4K UHD, 2 GB de RAM e 16 GB de armazenamento.",
      },
      es: {
        shortDesc:
          "Tope de la línea Allwinner — Android 7, 4K UHD, 2 GB de RAM y 16 GB de almacenamiento.",
      },
    },
    specs: {
      cpu: "Allwinner H3",
      ram: "2 GB DDR3",
      storage: "16 GB NAND Flash",
      os: "Android 7.0.1+",
      resolution: "4K UHD (2160p): H.265 up to 3840×2160 @ 30 fps, H.264 1080p @ 60 fps",
      videoCodecs: "HEVC H.265, H.264, MPEG1/2/4, RM/RMVB, VC-1, Xvid/DivX, RealVideo",
      audioCodecs: "MP3, WMA, AAC, WAV, OGG, AC3, MIDI, FLAC, APE, ALAC, M4A",
      hdmi: "HDMI v1.4",
      usb: "2× USB 2.0 Host",
      audioOut: "RCA via 3.5 mm mini-jack",
      network: "Ethernet 100 Mb/s",
      wifi: "2.4 GHz Wi-Fi b/g/n",
      cardReader: "microSD / TF",
      dimensions: "103 × 103 × 21 mm",
      power: "5 V / 2 A",
      accessories: "Remote, power adapter, HDMI cable, AV cable, quick start guide",
    },
  },
  {
    id: "tv3s",
    image: tv3sImg,
    year: 2019,
    en: {
      title: "inext TV3s",
      shortDesc: "Amlogic S905W 4K box with HDR10, running Android 6.",
    },
    localized: {
      uk: { shortDesc: "4K-приставка на Amlogic S905W з HDR10 під керуванням Android 6." },
      pl: { shortDesc: "Dekoder 4K na Amlogic S905W z HDR10, działający na Androidzie 6." },
      pt: { shortDesc: "Box 4K com Amlogic S905W e HDR10, rodando Android 6." },
      es: { shortDesc: "Box 4K con Amlogic S905W y HDR10, con Android 6." },
    },
    specs: {
      cpu: "Amlogic S905W Quad-Core 1.5 GHz",
      ram: "1 GB DDR3",
      storage: "8 GB NAND Flash",
      os: "Android 6.0.1+",
      resolution: "up to 4K UHD (2160p), HDR10 (BT.2020)",
      videoCodecs:
        "HEVC H.265 (10-bit), VP9 (10-bit), H.264, MPEG1/2/4, RM/RMVB, VC-1, Xvid/DivX, RealVideo",
      audioCodecs: "MP3, WMA, AAC, WAV, OGG, AC3, MIDI, DTS, FLAC, APE, ALAC, M4A",
      hdmi: "HDMI v2.0",
      usb: "2× USB 2.0 Host",
      audioOut: "5RCA via 3.5 mm mini-jack",
      network: "Ethernet 100 Mb/s",
      wifi: "2.4 GHz Wi-Fi b/g/n",
      cardReader: "microSD / TF",
      dimensions: "103 × 103 × 21 mm",
      power: "5 V / 2 A",
      accessories: "Remote, power adapter, HDMI cable, quick start guide",
    },
  },
  {
    id: "tv3",
    image: tv3Img,
    year: 2018,
    en: {
      title: "inext TV3",
      shortDesc: "Amlogic S905X 4K HDR10 box on Android 6 for OTT, IPTV and VOD.",
    },
    localized: {
      uk: { shortDesc: "Amlogic S905X, 4K HDR10 на Android 6 для OTT, IPTV та VOD." },
      pl: { shortDesc: "Amlogic S905X, 4K HDR10 na Androidzie 6 do OTT, IPTV i VOD." },
      pt: { shortDesc: "Amlogic S905X, 4K HDR10 no Android 6 para OTT, IPTV e VOD." },
      es: { shortDesc: "Amlogic S905X, 4K HDR10 en Android 6 para OTT, IPTV y VOD." },
    },
    specs: {
      cpu: "Amlogic S905X Quad-Core 1.5 GHz",
      ram: "1 GB DDR3",
      storage: "8 GB NAND Flash",
      os: "Android 6.0.1+",
      resolution: "up to 4K UHD (2160p), HDR10 (BT.2020)",
      videoCodecs: "HEVC H.265 (10-bit), VP9 (10-bit), H.264, MPEG1/2/4, RM/RMVB, VC-1, Xvid/DivX",
      audioCodecs: "MP3, WMA, AAC, WAV, OGG, AC3, MIDI, DTS, FLAC, APE, ALAC, M4A",
      hdmi: "HDMI v2.0",
      usb: "2× USB 2.0 Host",
      audioOut: "5RCA via 3.5 mm mini-jack",
      network: "Ethernet 100 Mb/s",
      wifi: "2.4 GHz Wi-Fi b/g/n",
      cardReader: "microSD / TF",
      dimensions: "103 × 103 × 21 mm",
      power: "5 V / 2 A",
      accessories: "Remote, power adapter, HDMI cable, quick start guide",
    },
  },
  {
    id: "4k2",
    image: tv4k2Img,
    year: 2017,
    en: {
      title: "inext 4K2",
      shortDesc: "Dual-band Wi-Fi and Bluetooth 4K box — Amlogic S905X, 2 GB RAM, Android 6.",
    },
    localized: {
      uk: {
        shortDesc:
          "4K-приставка з дводіапазонним Wi-Fi і Bluetooth — Amlogic S905X, 2 ГБ ОЗП, Android 6.",
      },
      pl: {
        shortDesc:
          "Dekoder 4K z dwuzakresowym Wi-Fi i Bluetooth — Amlogic S905X, 2 GB RAM, Android 6.",
      },
      pt: {
        shortDesc:
          "Box 4K com Wi-Fi dual-band e Bluetooth — Amlogic S905X, 2 GB de RAM, Android 6.",
      },
      es: {
        shortDesc:
          "Box 4K con Wi-Fi de doble banda y Bluetooth — Amlogic S905X, 2 GB de RAM, Android 6.",
      },
    },
    specs: {
      cpu: "Amlogic S905X Quad-Core 1.5 GHz",
      ram: "2 GB DDR3",
      storage: "8 GB eMMC Flash",
      os: "Android 6.0.1+",
      resolution: "4K UHD (2160p) up to 60 fps, HDR10 (BT.2020)",
      videoCodecs: "HEVC H.265 (10-bit), VP9 (10-bit), H.264, MPEG1/2/4, RMVB, VC-1, Xvid/DivX",
      audioCodecs: "MP3, WMA, AAC, WAV, OGG, AC3, MIDI, DTS, FLAC, APE, ALAC, M4A",
      hdmi: "HDMI v2.0",
      usb: "2× USB 2.0 Host",
      audioOut: "5RCA via 3.5 mm mini-jack",
      network: "Ethernet 100 Mb/s",
      wifi: "Dual-band 2.4 / 5 GHz Wi-Fi b/g/n/ac",
      bluetooth: "Bluetooth 4.1",
      cardReader: "microSD / TF",
      dimensions: "100 × 100 × 20 mm",
      power: "5 V / 2 A",
      accessories: "Remote, power adapter, HDMI cable, quick start guide",
    },
  },
  {
    id: "tv2er2",
    image: tv2er2Img,
    year: 2017,
    en: {
      title: "inext TV2er2",
      shortDesc: "Full HD H.265 box with four USB ports — Amlogic S805, Android 5.1.",
    },
    localized: {
      uk: {
        shortDesc: "Full HD-приставка з H.265 та чотирма USB-портами — Amlogic S805, Android 5.1.",
      },
      pl: {
        shortDesc: "Dekoder Full HD z H.265 i czterema portami USB — Amlogic S805, Android 5.1.",
      },
      pt: {
        shortDesc: "Box Full HD com H.265 e quatro portas USB — Amlogic S805, Android 5.1.",
      },
      es: {
        shortDesc: "Box Full HD con H.265 y cuatro puertos USB — Amlogic S805, Android 5.1.",
      },
    },
    specs: {
      cpu: "Amlogic S805",
      ram: "1 GB DDR3",
      storage: "8 GB NAND Flash",
      os: "Android 5.1.1",
      resolution: "up to Full HD (1080p) @ 60 fps",
      videoCodecs: "HEVC H.265 (10-bit), H.264, MPEG1/2/4, VC-1, Xvid/DivX, RealVideo",
      audioCodecs: "MP3, WMA, AAC, WAV, OGG, AC3, MIDI, DTS, FLAC, APE, ALAC, M4A",
      hdmi: "HDMI v2.0",
      usb: "4× USB 2.0 Host",
      audioOut: "S/PDIF & RCA via 3.5 mm mini-jack",
      network: "Ethernet 100 Mb/s",
      wifi: "2.4 GHz Wi-Fi b/g/n",
      cardReader: "SD / MMC",
      dimensions: "110 × 110 × 26 mm",
      power: "5 V / 2 A",
      accessories: "Remote, power adapter, HDMI cable, quick start guide",
    },
  },
  {
    id: "4k",
    image: tv4kImg,
    year: 2017,
    en: {
      title: "inext 4K",
      shortDesc: "Amlogic S905X 4K box with Wi-Fi ac and Bluetooth, Android 6.",
    },
    localized: {
      uk: { shortDesc: "Amlogic S905X, 4K із Wi-Fi ac та Bluetooth, Android 6." },
      pl: { shortDesc: "Amlogic S905X, 4K z Wi-Fi ac i Bluetooth, Android 6." },
      pt: { shortDesc: "Amlogic S905X, 4K com Wi-Fi ac e Bluetooth, Android 6." },
      es: { shortDesc: "Amlogic S905X, 4K con Wi-Fi ac y Bluetooth, Android 6." },
    },
    specs: {
      cpu: "Amlogic S905X Quad-Core 1.5 GHz",
      ram: "2 GB DDR3",
      storage: "8 GB eMMC",
      os: "Android 6.0.1",
      resolution: "up to 4K UHD (2160p) @ 60 fps",
      videoCodecs: "HEVC H.265 (10-bit), VP9 (10-bit), H.264, MPEG1/2/4, RMVB, VC-1, Xvid/DivX",
      audioCodecs: "MP3, WMA, AAC, WAV, OGG, AC3, MIDI, FLAC, APE, ALAC, M4A",
      hdmi: "HDMI v2.0",
      usb: "2× USB 2.0 Host",
      audioOut: "5RCA via 3.5 mm mini-jack",
      network: "Ethernet 100 Mb/s",
      wifi: "Wi-Fi b/g/n/ac",
      bluetooth: "Bluetooth 4.1",
      cardReader: "microSD / TF",
      dimensions: "110 × 110 × 30 mm",
      power: "5 V / 2 A",
      accessories: "Remote, power adapter, HDMI cable, quick start guide",
    },
  },
  {
    id: "tv2e",
    image: tv2eImg,
    year: 2016,
    en: {
      title: "inext TV2e",
      shortDesc: "Full HD Amlogic S805 box with four USB ports, Android 5.1.",
    },
    localized: {
      uk: { shortDesc: "Full HD-приставка на Amlogic S805 із чотирма USB-портами, Android 5.1." },
      pl: { shortDesc: "Dekoder Full HD na Amlogic S805 z czterema portami USB, Android 5.1." },
      pt: { shortDesc: "Box Full HD com Amlogic S805 e quatro portas USB, Android 5.1." },
      es: { shortDesc: "Box Full HD con Amlogic S805 y cuatro puertos USB, Android 5.1." },
    },
    specs: {
      cpu: "Amlogic S805",
      ram: "1 GB DDR3",
      storage: "8 GB NAND Flash",
      os: "Android 5.1.1",
      resolution: "up to Full HD (1080p) @ 60 fps",
      videoCodecs: "HEVC H.265 (10-bit), H.264, MPEG1/2/4, RM/RMVB, VC-1, Xvid/DivX, RealVideo",
      audioCodecs: "MP3, WMA, AAC, WAV, OGG, AC3, MIDI, DTS, FLAC, APE, ALAC, M4A",
      hdmi: "HDMI v2.0",
      usb: "4× USB 2.0",
      audioOut: "S/PDIF & RCA via 3.5 mm mini-jack",
      network: "Ethernet 100 Mb/s",
      wifi: "2.4 GHz Wi-Fi b/g/n",
      cardReader: "SD / MMC",
      dimensions: "110 × 110 × 26 mm",
      power: "5 V / 2 A",
      accessories: "Remote, power adapter, HDMI cable, quick start guide",
    },
  },
  {
    id: "tv2",
    image: tv2Img,
    year: 2016,
    en: {
      title: "inext TV2",
      shortDesc: "The original Full HD Amlogic S805 box on Android 5.1.",
    },
    localized: {
      uk: { shortDesc: "Перша Full HD-приставка на Amlogic S805 під керуванням Android 5.1." },
      pl: { shortDesc: "Pierwszy dekoder Full HD na Amlogic S805 z Androidem 5.1." },
      pt: { shortDesc: "O primeiro box Full HD com Amlogic S805 rodando Android 5.1." },
      es: { shortDesc: "El primer box Full HD con Amlogic S805 en Android 5.1." },
    },
    specs: {
      cpu: "Amlogic S805",
      ram: "1 GB DDR3",
      storage: "8 GB NAND Flash",
      os: "Android 5.1.1",
      resolution: "up to Full HD (1080p) @ 60 fps",
      videoCodecs: "HEVC H.265 (10-bit), H.264, MPEG1/2/4, RM/RMVB, VC-1, Xvid/DivX, RealVideo",
      audioCodecs: "MP3, WMA, AAC, WAV, OGG, AC3, MIDI, DTS, FLAC, APE, ALAC, M4A",
      hdmi: "HDMI v1.4b",
      usb: "1× USB 2.0 Host",
      audioOut: "S/PDIF & RCA via 3.5 mm mini-jack",
      network: "Ethernet 100 Mb/s",
      wifi: "2.4 GHz Wi-Fi b/g/n",
      cardReader: "SD / MMC",
      dimensions: "130 × 130 × 36 mm",
      power: "5 V / 2 A",
      accessories: "Remote, power adapter, HDMI cable, quick start guide",
    },
  },
  {
    id: "tv",
    image: tvImg,
    year: 2015,
    en: {
      title: "inext TV",
      shortDesc: "Linux media player on a Realtek RTD1185DD with Dolby and DTS licences.",
    },
    localized: {
      uk: { shortDesc: "Медіаплеєр на Linux і Realtek RTD1185DD з ліцензіями Dolby та DTS." },
      pl: {
        shortDesc:
          "Odtwarzacz multimedialny na Linuksie i Realtek RTD1185DD z licencjami Dolby i DTS.",
      },
      pt: {
        shortDesc: "Media player Linux em um Realtek RTD1185DD com licenças Dolby e DTS.",
      },
      es: {
        shortDesc:
          "Reproductor multimedia Linux en un Realtek RTD1185DD con licencias Dolby y DTS.",
      },
    },
    specs: {
      cpu: "Realtek RTD1185DD",
      ram: "256 MB DDR2",
      storage: "256 MB",
      os: "Linux",
      resolution: "1920×1080 (Full HD 1080p)",
      videoCodecs: "H.264, MPEG1/2/4, DivX, Xvid, WMV9, VC-1, RealVideo, M-JPEG, AVS HD",
      audioCodecs:
        "Dolby Digital TrueHD, Dolby Digital Plus, DTS-HD Master Audio, FLAC, AAC, MP3, WMA, PCM, ADPCM",
      hdmi: "HDMI v1.3",
      usb: "2× USB 2.0 Host",
      audioOut: "A/V out (CVBS), S/PDIF (coaxial)",
      network: "Ethernet 100 Mb/s",
      dimensions: "—",
      accessories: "Remote, power adapter, HDMI cable, quick start guide",
    },
  },
  {
    id: "3d-kid",
    image: kid3dImg,
    year: 2012,
    en: {
      title: "inext 3D Kid",
      shortDesc: "3D-capable Full HD media player — Realtek RTD1186DD, Gigabit LAN, Android 2.2.",
    },
    localized: {
      uk: {
        shortDesc:
          "Full HD-медіаплеєр із підтримкою 3D — Realtek RTD1186DD, гігабітний LAN, Android 2.2.",
      },
      pl: {
        shortDesc: "Odtwarzacz Full HD z obsługą 3D — Realtek RTD1186DD, Gigabit LAN, Android 2.2.",
      },
      pt: {
        shortDesc:
          "Media player Full HD com suporte a 3D — Realtek RTD1186DD, LAN Gigabit, Android 2.2.",
      },
      es: {
        shortDesc:
          "Reproductor multimedia Full HD con soporte 3D — Realtek RTD1186DD, LAN Gigabit, Android 2.2.",
      },
    },
    specs: {
      cpu: "Realtek RTD1186DD 800 MHz",
      ram: "512 MB DDR3",
      storage: "2 GB NAND Flash",
      os: "Linux + Android 2.2",
      resolution: "Full HD (1920×1080); 3D: Side-by-Side, Top & Bottom, Frame Packing",
      videoCodecs: "MPEG1/2/4, H.264, Xvid, WMV9/VC-1, RM/RMVB",
      audioCodecs:
        "Dolby Digital TrueHD, Dolby Digital Plus, DTS-HD Master Audio, MP3, WMA, RA, RM, OGG",
      hdmi: "HDMI v1.4",
      usb: "2× USB 2.0 Host",
      audioOut: "Composite, analog stereo, S/PDIF (optical)",
      network: "Ethernet 10/100/1000 Mb/s",
      wifi: "Wi-Fi 802.11n",
      cardReader: "Built-in card reader",
      accessories: "Remote, power adapter, AV cable, instructions",
    },
  },
  {
    id: "hd1",
    image: hd1Img,
    year: 2011,
    en: {
      title: "inext HD1",
      shortDesc: "The first inext box — a Full HD Realtek player with a 3.5″ SATA HDD bay.",
    },
    localized: {
      uk: {
        shortDesc:
          "Перша приставка inext — Full HD-плеєр на Realtek із відсіком для 3,5″ SATA-диска.",
      },
      pl: {
        shortDesc:
          "Pierwszy dekoder inext — odtwarzacz Full HD na Realteku z wnęką na dysk 3,5″ SATA.",
      },
      pt: {
        shortDesc:
          "O primeiro box inext — um player Full HD da Realtek com baia para HD SATA de 3,5″.",
      },
      es: {
        shortDesc:
          "El primer box inext — un reproductor Full HD de Realtek con bahía para disco SATA de 3,5″.",
      },
    },
    specs: {
      cpu: "Realtek RTD1186DD 800 MHz",
      ram: "512 MB DDR3",
      storage: "2 GB NAND Flash + 3.5″ SATA / SATA II HDD bay",
      os: "Android 2.2",
      resolution: "Full HD (1920×1080)",
      videoCodecs: "MPEG1, HD MPEG2/4, H.264, Xvid, WMV9/VC-1, RM/RMVB, AVS HD",
      audioCodecs:
        "Dolby Digital TrueHD, Dolby Digital Plus, DTS-HD Master Audio, MP3, WMA, RA, RM, OGG",
      hdmi: "HDMI v1.4",
      usb: "2× USB 2.0 Host, USB 3.0 Slave",
      audioOut: "Composite, analog stereo, S/PDIF (optical, coaxial)",
      network: "Wi-Fi (USB adapter)",
      cardReader: "SD / SDHC",
      accessories:
        "USB 3.0 cable, HDMI cable, Wi-Fi USB adapter, remote, AV cable, power supply, batteries, instructions",
    },
  },
];
